/**
 * Which tab of an inspector is open, remembered per kind of section.
 *
 * Somebody setting the look of one chart after another wants the look tab
 * each time, not the figures they already typed. So the last tab chosen for a
 * kind is the one the next section of that kind opens on, on this browser only,
 * like the theme and the page view. Storage that is blocked or cleared only
 * costs the memory: the first tab opens.
 */

import { ref, watch, type Ref } from 'vue'

const PREFIX = 'dmbuilder.inspectorTab.'

function recalled(key: string): string | null {
  try {
    return window.localStorage.getItem(PREFIX + key)
  } catch {
    return null
  }
}

function remember(key: string, tab: string): void {
  try {
    window.localStorage.setItem(PREFIX + key, tab)
  } catch {
    // The choice still holds while the inspector is open.
  }
}

/** The open tab, one of `tabs`: the one last chosen for `key`, or the first. */
export function useRememberedTab<T extends string>(key: string, tabs: readonly T[]): Ref<T> {
  const stored = recalled(key)
  const first = tabs[0] as T
  const open = ref((tabs as readonly string[]).includes(stored ?? '') ? stored : first) as Ref<T>
  watch(open, (tab) => remember(key, tab))
  return open
}
